import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const STEP = 1;
const SKIP_STEP = 5;

/**
 * An "Add to cart" button that morphs into an inline (<< - qty + >>) stepper
 * once the variant is in the cart, so the buyer can keep adjusting quantity
 * without leaving the product page. The quantity value is also editable by
 * keyboard.
 */
class NbCartStepper extends HTMLElement {
  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.max = this.dataset.max ? parseInt(this.dataset.max, 10) : null;
    this.min = this.dataset.min ? parseInt(this.dataset.min, 10) : 1;
    this.quantity = parseInt(this.dataset.quantity || '0', 10);
    this.lineKey = this.lineKey ?? null;
    this.busy = false;

    this.addForm = this.querySelector('[data-role="add-form"]');
    this.addButton = this.querySelector('[data-role="add"]');
    this.stepper = this.querySelector('[data-role="stepper"]');
    this.valueEl = this.querySelector('[data-role="value"]');
    this.decreaseButton = this.querySelector('[data-role="decrease"]');
    this.increaseButton = this.querySelector('[data-role="increase"]');
    this.decreaseSkipButton = this.querySelector('[data-role="decrease-skip"]');
    this.increaseSkipButton = this.querySelector('[data-role="increase-skip"]');

    this.addForm?.addEventListener('submit', this.handleAdd);
    this.decreaseButton?.addEventListener('click', this.handleDecrease);
    this.increaseButton?.addEventListener('click', this.handleIncrease);
    this.decreaseSkipButton?.addEventListener('click', this.handleDecreaseSkip);
    this.increaseSkipButton?.addEventListener('click', this.handleIncreaseSkip);
    this.valueEl?.addEventListener('focus', this.handleValueFocus);
    this.valueEl?.addEventListener('keydown', this.handleValueKeydown);
    this.valueEl?.addEventListener('blur', this.handleValueBlur);

    this.render();
  }

  disconnectedCallback() {
    this.addForm?.removeEventListener('submit', this.handleAdd);
    this.decreaseButton?.removeEventListener('click', this.handleDecrease);
    this.increaseButton?.removeEventListener('click', this.handleIncrease);
    this.decreaseSkipButton?.removeEventListener('click', this.handleDecreaseSkip);
    this.increaseSkipButton?.removeEventListener('click', this.handleIncreaseSkip);
    this.valueEl?.removeEventListener('focus', this.handleValueFocus);
    this.valueEl?.removeEventListener('keydown', this.handleValueKeydown);
    this.valueEl?.removeEventListener('blur', this.handleValueBlur);
  }

  /**
   * Re-points this control at a different variant without re-rendering it.
   * Used by the paint colour picker, which switches variant client side and
   * tracks lines by key because the same variant can sit in the cart once per
   * colour (same variant id, different line item properties).
   *
   * @param {{ variantId: string | number, quantity?: number, lineKey?: string | null, min?: number, max?: number | null }} config
   */
  configure({ variantId, quantity = 0, lineKey = null, min = 1, max = null }) {
    this.variantId = String(variantId);
    this.dataset.variantId = this.variantId;
    this.quantity = quantity;
    this.lineKey = lineKey;
    this.min = min;
    this.max = max;
    this.render();
  }

  /**
   * Line item properties from `properties[...]` inputs inside the add form,
   * or null when there are none (every non-paint form).
   *
   * @returns {Record<string, string> | null}
   */
  formProperties() {
    const inputs = this.addForm?.querySelectorAll('input[name^="properties["]') ?? [];
    /** @type {Record<string, string>} */
    const properties = {};
    let found = false;
    for (const input of inputs) {
      const match = /^properties\[(.+)\]$/.exec(input.name);
      if (!match || !input.value) continue;
      properties[match[1]] = input.value;
      found = true;
    }
    return found ? properties : null;
  }

  render() {
    const inCart = this.quantity > 0;
    this.addForm?.toggleAttribute('hidden', inCart);
    this.stepper?.toggleAttribute('hidden', !inCart);

    if (this.valueEl) this.valueEl.value = String(this.quantity);

    if (this.increaseButton) {
      this.increaseButton.disabled = this.max != null && this.quantity >= this.max;
    }
    if (this.increaseSkipButton) {
      this.increaseSkipButton.disabled = this.max != null && this.quantity >= this.max;
    }
    if (this.decreaseSkipButton) {
      this.decreaseSkipButton.disabled = this.quantity <= this.min;
    }
  }

  handleAdd = (event) => {
    event.preventDefault();
    if (this.busy || this.addButton?.disabled) return;
    this.mutate('add', this.quantity + 1);
  };

  handleIncrease = (event) => {
    event.preventDefault();
    if (this.busy) return;
    if (this.max != null && this.quantity >= this.max) return;
    this.mutate('change', this.quantity + STEP);
  };

  handleDecrease = (event) => {
    event.preventDefault();
    if (this.busy) return;
    this.mutate('change', Math.max(0, this.quantity - STEP));
  };

  handleIncreaseSkip = (event) => {
    event.preventDefault();
    if (this.busy) return;
    let next = this.quantity + SKIP_STEP;
    if (this.max != null) next = Math.min(this.max, next);
    if (next === this.quantity) return;
    this.mutate('change', next);
  };

  handleDecreaseSkip = (event) => {
    event.preventDefault();
    if (this.busy) return;
    const next = Math.max(this.min, this.quantity - SKIP_STEP);
    if (next === this.quantity) return;
    this.mutate('change', next);
  };

  handleValueFocus = (event) => {
    event.target.select();
  };

  handleValueKeydown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    } else if (event.key === 'Escape') {
      event.target.value = String(this.quantity);
      event.target.blur();
    }
  };

  handleValueBlur = (event) => {
    if (this.busy) {
      this.render();
      return;
    }

    const parsed = parseInt(event.target.value.trim(), 10);

    if (!Number.isFinite(parsed)) {
      this.render();
      return;
    }

    let next = Math.max(this.min, parsed);
    if (this.max != null) next = Math.min(this.max, next);

    if (next === this.quantity) {
      this.render();
      return;
    }

    this.mutate('change', next);
  };

  async mutate(kind, nextQuantity) {
    this.busy = true;
    this.classList.add('nb-cart-stepper--busy');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: nextQuantity === 0 ? 'remove' : kind === 'add' ? 'add' : 'update',
        context: 'product',
        lines: [{ merchandiseId: this.variantId, quantity: nextQuantity }],
        promise: deferred.promise,
      })
    );

    try {
      const endpoint = kind === 'add' ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
      const properties = kind === 'add' ? this.formProperties() : null;
      // With properties, the same variant can be on several lines, so later
      // changes must target this line by key rather than by variant id.
      const body =
        kind === 'add'
          ? { id: this.variantId, quantity: 1, ...(properties && { properties }) }
          : { id: this.lineKey || this.variantId, quantity: nextQuantity };

      const response = await fetch(endpoint, fetchConfig('json', { body: JSON.stringify(body) }));
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Cart update failed');
      }

      if (properties && data.key) this.lineKey = data.key;

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      this.quantity = nextQuantity;
      this.render();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { itemCount: cart.item_count, source: 'nb-cart-stepper' },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Cart update failed',
          code: 'INVALID',
        })
      );
      this.render();
    } finally {
      this.busy = false;
      this.classList.remove('nb-cart-stepper--busy');
    }
  }
}

if (!customElements.get('nb-cart-stepper')) {
  customElements.define('nb-cart-stepper', NbCartStepper);
}
